import React, { useState } from 'react';
import './BuyerUploadTile.scss';
import { faFileAlt } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';
import ConfirmationModal from '../../../../../components/Modal/ConfirmationModal';
import EditableTableCell from '../../../../CorporateGroupManagement/EditableTableCell/EditableTableCell';
import { MarketplaceBuyerUpload } from '../../../../../slices/marketplace/buyer/models/buyer-upload';
import createNotification from '../../../../../utils/createNotification';
import { useStateSelector } from '../../../../../store/selectors';
import { useDispatch } from 'react-redux';
import { GetBuyer } from '../../../../../slices/marketplace/buyer/thunks/get-buyer.thunk';
import PortalTooltip from '../../../../../components/PortalTooltip/PortalTooltip';
import { objectToQueryString } from '../../../../../utils/queryHelper';

export interface BuyerUploadTileProps {
    upload: MarketplaceBuyerUpload;
    isReadonly?: boolean;
}

export const BuyerUploadTile = (props: BuyerUploadTileProps) => {
    const isMarketplaceBuyer = useStateSelector(
        (s) => s.userSummary?.summary?.isMarketplaceBuyer
    );
    const buyerId = useStateSelector((s) => s.buyersState.targetBuyer.id);

    const [isConfirmationVisible, setIsConfirmationVisible] =
        useState<boolean>(false);
    const axios = useStateSelector((state) => state.core.axios);
    const dispatch = useDispatch() as any;

    const fetchBuyerProfile = async () => {
        await dispatch(GetBuyer(null));
    };

    const downloadFile = (documentId: number) => {
        const queryParams = isMarketplaceBuyer
            ? ''
            : objectToQueryString({ buyerId });

        axios
            .get(
                `/api/Marketplace/MarketplaceBuyerUploads/${documentId}?${queryParams}`
            )
            .then((response) => {
                if (response.status === 200) {
                    window.open(response.data, '_blank');
                }
            })
            .catch(() =>
                createNotification(
                    'Some unexpected error occured while downloading file.',
                    'error'
                )
            );
    };

    const deleteFile = (documentId: number) => {
        axios
            .delete(`/api/Marketplace/MarketplaceBuyerUploads/${documentId}`)
            .catch(() =>
                createNotification(
                    'Some unexpected error occured while deleting file.',
                    'error'
                )
            )
            .finally(() => {
                fetchBuyerProfile();
            });
    };

    const updateFileName = (
        document: MarketplaceBuyerUpload,
        fileName: string
    ) => {
        const extension = document.name.substring(
            document.name.lastIndexOf('.')
        );
        const obj = {
            id: document.id,
            accessLevel: document.accessLevel,
            name: `${fileName}${extension}`,
            source: document.source,
        };
        axios
            .put(`/api/Marketplace/MarketplaceBuyerUploads`, obj)
            .catch(() =>
                createNotification(
                    'Some unexpected error occured while deleting file.',
                    'error'
                )
            )
            .finally(() => {
                fetchBuyerProfile();
            });
    };

    const getTooltipId = () => {
        return 'document-' + props.upload.id.toString() + '-id';
    };

    return (
        <div className="upload-tile">
            <div className="icon-container">
                <FontAwesomeIcon
                    inverse
                    onClick={() => {
                        downloadFile(props.upload.id);
                    }}
                    className="icon"
                    icon={faFileAlt as any}></FontAwesomeIcon>
            </div>
            <div className="file-info">
                {props.isReadonly ? (
                    <div
                        className="editable-cell-view not-editable-cell"
                        id={getTooltipId()}>
                        <div
                            onClick={() => {
                                downloadFile(props.upload.id);
                            }}
                            className="link-text">
                            {props.upload.name}
                        </div>{' '}
                        <PortalTooltip
                            anchorId={getTooltipId()}
                            delayShow={1000}
                            place="top"
                            content={props.upload.name}
                        />
                    </div>
                ) : (
                    <>
                        <EditableTableCell
                            id={getTooltipId()}
                            cellValue={props.upload.name.replace(
                                /\.[^/.]+$/,
                                ''
                            )}
                            cellViewContent={<>{props.upload.name}</>}
                            updateCellValue={(newName: string) => {
                                updateFileName(props.upload, newName);
                                return true;
                            }}
                            isDeleteAvailable={true}
                            deleteIcon={faTrashCan}
                            deleteIconClasses="delete-button"
                            onDeleteClick={() => {
                                setIsConfirmationVisible(true);
                            }}
                            onTextClick={() => downloadFile(props.upload.id)}
                        />
                    </>
                )}
            </div>
            {isConfirmationVisible && (
                <ConfirmationModal
                    isVisible={isConfirmationVisible}
                    setIsVisible={() => {
                        setIsConfirmationVisible(false);
                    }}
                    header={'Are you sure?'}
                    confirmationText={`Are you sure you want to delete ${props.upload.name}?`}
                    onConfirm={() => {
                        deleteFile(props.upload.id);
                    }}
                    params={undefined}
                    cancelButtonText={'No, take me back'}
                    nextButtonText={`Yes, I'm sure`}></ConfirmationModal>
            )}
        </div>
    );
};
